import React, { useEffect, useState, useRef, useContext, useLayoutEffect } from 'react';
import styled from 'styled-components/macro';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import AdvancedVariantsTable from '@mirinae/lessons/components/lesson/elements/AdvancedVariantsTable';
import PageElements from '@mirinae/lessons/components/lesson/PageElements';
import { UserAgent } from '@mirinae/js-utils';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import { classes } from '@mirinae/react-ui';
import { PageTop } from './DefaultPage';
import TranslateOffIcon from '@mirinae/lessons/assets/images/translate-icon-off.svg';
import TranslateOnIcon from '@mirinae/lessons/assets/images/translate-icon-on.svg';
import TranslateSwitchOnIcon from '@mirinae/lessons/assets/images/translate-on.svg';
import TranslateSwitchOffIcon from '@mirinae/lessons/assets/images/translate-off.svg';

const isMobile = !UserAgent.os.isOthers;

const EntryStage = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const EntryCarousel = styled.div`
    position: relative;
    width: ${props => props.geom.width}px;
    
    .${classes.desktop} & {
        overflow-x: clip;
        overflow-y: visible;
        height: ${props => props.geom.height}px;
    }
    
    .${classes.mobile} & {
        // height: fit-content;
        overflow-x: hidden;
        overflow-y: auto;
        height: ${props => props.geom.height}px;
        margin-bottom: 15px;
    }
`;

const EntryRow = styled.div`
    display: flex;
    flex-direction: row;
    & > div {
        margin-right: ${props => props.entryGap}px;
    }
`;

const EntryScroller = styled.div`
    position: absolute;
    left: ${props => props.left}px;
    width: ${props => props.geom.width}px;
    height: ${props => props.geom.height}px;
    touch-action: none;
    ${props => props.numEntries < 1 && (`
        width: 100%;
        & div {
            justify-content: start;
        }
    `)}
`;

const Dots = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    width: auto;
    justify-content: center;
    align-items: center;
    margin-top: ${isMobile ? 34 : 25}px;
    
    div + div {
        margin-left: 15px;
    }
`;

const Dot = styled.div`
    background-color: ${props => (props.isCurrent ? lessonsPalette.primary.main : lessonsPalette.grey[5])};
    border-radius: 50%;
    width: 8px; height: 8px;
    padding: 0 4px;
    cursor: pointer;
`;

const TranslateSwitch = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    position: absolute;
    
    color: ${props => (props.on ? '#48A9A6' : '#3c3c3b')};
    font-size: 14px;
    font-weight: 500;
    
    .${classes.desktop} & {
        right: 70px;
        bottom: 34px;
    }
    .${classes.mobile} & {
        //margin-left: 20px;
        //transform: translate(0, -5px);
        right: 0px;
        bottom: 25px;
    }
`;

const TranslationToggle = styled.div`
    transform: translate(0, 2px);
    cursor: pointer;
`;

const AvancedPatternVariants = ({ page }) => {
    const [variant, setVariant] = useState(null);
    const [exampleEntries, setexampleEntries] = useState([]);
    const [placementTable, setPlacementTable] = useState(null);
    const [translation, setTranslation] = useState(false);

    const player = useContext(PlayerContext);
    const entryType = {
        component: AdvancedVariantsTable,
        width: 707,
        gap: 142,
    };

    const toggleTranslation = () => {
        setTranslation(!translation);
    };

    useEffect(() => {
        const firstVariant = page.children[0].children.find(e => e.type === 'advancedVariantEntry');
        setVariant(firstVariant); // only display examples for first variant entry element on the page, each variant must be a new page
        const exampleEntries = firstVariant.fields.filter(f => f.type === 'parts' && !f.hidden);
        if (exampleEntries.length > 0) {
            setexampleEntries(exampleEntries);
        }

        page.children.forEach(step => step.children.filter(e => e.type === 'advancedVariantsTable').forEach(e => setPlacementTable(e)));

        return () => {
            player.registerNavigation(null);
        };
    }, []);

    // useLayoutEffect(() => {
    //     if (placementTable && placementTable.layout) {
    //         Object.assign(placementTable.layout.row.margin, { left: 0, right: 0 }); // force margins to screen edge on desktop for full-width carousel
    //     }
    // }, [placementTable]);

    // desktop pages have a carousel of swipable entries, centering & showing one entry at time
    const DesktopCarousel = () => {
        const [currentEntry, setCurrentEntry] = useState(0);
        const scroller = useRef(null);
        const perEntryOffset = () => entryType.width + entryType.gap;

        player.registerNavigation({
            skipOrNext: () => (currentEntry < exampleEntries.length - 1 ? 'skipAndNext' : 'next'),
            back: () => (currentEntry === 0 ? player.back(true) : gotoEntry(currentEntry - 1)),
            next: () => (currentEntry < exampleEntries.length - 1 ? gotoEntry(currentEntry + 1) : player.next(true)),
        });

        const entryOffset = (entry) => -entry * perEntryOffset() + placementTable.layout.row.left;

        const gotoEntry = (i) => {
            scroller.current.style.transition = 'left 0.25s ease-out';
            scroller.current.style.left = `${entryOffset(i)}px`;
            setCurrentEntry(i);
        };

        return (
            <EntryStage>
                <EntryCarousel geom={{ width: page.lessonBounds.width - 2, height: placementTable.geom.height + 10 }}>
                    <EntryScroller
                        ref={scroller} left={exampleEntries.length > 0 ? placementTable.layout.row.left : 0} numEntries={exampleEntries.length}
                        geom={{ left: 75, height: placementTable.geom.height, width: exampleEntries.length * perEntryOffset() }}
                    >
                        <EntryRow entryGap={exampleEntries.length > 1 ? entryType.gap : 0}>
                            { exampleEntries.map(ex => <entryType.component key={ex.index} element={placementTable} variant={variant} example={ex} translation={translation}/>) }
                        </EntryRow>
                    </EntryScroller>
                </EntryCarousel>
                <Dots>
                    { Array.from({ length: Math.ceil(exampleEntries.length / 1) }).map((ex, i) => <Dot key={i} isCurrent={currentEntry / 1 === i} onClick={() => gotoEntry(i * 1)} />) }
                </Dots>
                <TranslateSwitch on={translation}>
                    <img src={translation ? TranslateOnIcon : TranslateOffIcon} alt="TranslateIcon" />
                    Translation
                    <TranslationToggle onClick={toggleTranslation}>
                        <img src={translation ? TranslateSwitchOnIcon : TranslateSwitchOffIcon} alt="TranslateSwitchOnIcon" />
                    </TranslationToggle>
                </TranslateSwitch>
            </EntryStage>
        );
    };

    // mobile letter pages have a carousel of swipable entrys, centering & showing one entry at time
    const MobileCarousel = () => {
        const [touchStart, setTouchStart] = useState(0);
        const [touchEnd, setTouchEnd] = useState(0);
        const [currentEntry, setCurrentEntry] = useState(0);
        const scroller = useRef(null);
        const perEntryOffset = () => placementTable.layout.row.width + placementTable.layout.row.margin.right;

        player.registerNavigation({
            skipOrNext: () => (currentEntry < exampleEntries.length - 1 ? 'skipAndNext' : 'next'),
            back: () => (currentEntry === 0 ? player.back(true) : gotoEntry(currentEntry - 1)),
            next: () => (currentEntry < exampleEntries.length - 1 ? gotoEntry(currentEntry + 1) : player.next(true)),
        });

        const entryOffset = (entry) => -entry * perEntryOffset();

        const onTouchStart = (e) => {
            scroller.current.style.transition = 'left 0s linear';
            const touchX = e.targetTouches[0].clientX;
            setTouchStart(touchX);
            setTouchEnd(touchX);
        };

        const onTouchMove = (e) => {
            const end = e.targetTouches[0].clientX;
            setTouchEnd(end);
            if (scroller.current)
                scroller.current.style.left = `${entryOffset(currentEntry) + (end - touchStart)}px`;
        };

        const onTouchEnd = () => {
            if (scroller.current) {
                scroller.current.style.transition = 'left 0.25s ease-out';
                let entry = currentEntry;
                if (touchStart - touchEnd > 50 && currentEntry < exampleEntries.length - 1)
                    entry += 1;
                else if (touchStart - touchEnd < -50 && currentEntry >= 1)
                    entry -= 1;
                scroller.current.style.left = `${entryOffset(entry)}px`;
                setCurrentEntry(entry);
            }
        };

        const gotoEntry = (i) => {
            scroller.current.style.transition = 'left 0.25s ease-out';
            scroller.current.style.left = `${entryOffset(i)}px`;
            setCurrentEntry(i);
        };

        return (
            <EntryStage>
                <EntryCarousel geom={{ width: placementTable.layout.row.width, height: placementTable.layout.row.height - 20 }}>
                    <EntryScroller
                        ref={scroller}
                        geom={{ width: exampleEntries.length * perEntryOffset() }} left={0}
                        // onTouchStart={onTouchStart}
                        // onTouchMove={onTouchMove}
                        // onTouchEnd={onTouchEnd}
                    >
                        <EntryRow entryGap={placementTable.layout.row.margin.right}>
                            { exampleEntries.map(ex => <entryType.component key={ex.index} element={placementTable} variant={variant} example={ex} translation={translation} />) }
                        </EntryRow>
                    </EntryScroller>
                </EntryCarousel>
                <Dots>
                    { exampleEntries.map((vc, i) => <Dot key={i} isCurrent={currentEntry === i} onClick={() => gotoEntry(i)} />) }
                    <TranslateSwitch>
                        <img src={translation ? TranslateOnIcon : TranslateOffIcon} alt="TranslateIcon" />
                        Translation
                        <TranslationToggle onClick={toggleTranslation}>
                            <img src={translation ? TranslateSwitchOnIcon : TranslateSwitchOffIcon} alt="TranslateSwitchOnIcon" />
                        </TranslationToggle>
                    </TranslateSwitch>
                </Dots>
            </EntryStage>
        );
    };

    // ignore auto-layout of variantEntries
    const displayFilter = (e) => !e.componentName.match(/advancedVariantsEntry|advancedVariantEntry/) || e.nodeID === placementTable.nodeID;

    // replace the placement-letter entry with a carousel showing all exampleEntries
    const mapComponent = (e) => {
        if (e.nodeID === placementTable.nodeID) {
            if (isMobile) return MobileCarousel;
            else {
                if (e.layout)
                    Object.assign(e.layout.row.margin, { left: 0, right: 0 }); // force margins to screen edge on desktop for full-width carousel
                return DesktopCarousel;
            }
        }
    };

    return (
        <>
            <PageTop page={page} />
            { exampleEntries.length > 0 && <PageElements page={page} displayFilter={displayFilter} mapComponent={mapComponent} /> }
        </>
    );
};

export default AvancedPatternVariants;
